import React from 'react';
import { useState } from "react"
import axiosClient from "../../services/axiosClient"
import { Card, CardContent, Typography, Button, Chip, Box, Tabs, Tab, CircularProgress,Alert,Grid,
  Divider,IconButton,Tooltip, Paper, Link, TextField} from "@mui/material"
import { CheckCircle, Pending, Refresh, Analytics } from "@mui/icons-material"
import { usePage } from "@inertiajs/react"
import { router } from '@inertiajs/react'
import { formatPhoneNumber } from '../../utils/formatPhoneNumber';



interface Task {
  id: string
  action_type: string
  done: boolean
  done_by?: string
  executed_at?: string
  listing_id?: string
  created_at?: string
  updated_at?: string

  should_post_on_zillow?: boolean
  should_post_on_facebook?: boolean
  should_post_on_apartments?: boolean
  should_post_on_craiglist?: boolean

  zillow_listing_link?: string | null
  facebook_listing_link?: string | null
  apartments_listing_link?: string | null
  craiglist_listing_link?: string | null

  zillow_listing_date?: string | null
  facebook_listing_date?: string | null
  apartments_listing_date?: string | null
  craiglist_listing_date?: string | null

  all_tasks_complete?: boolean
  stale?: boolean

  prospect?: {
    email: string
    name: string
    phone_number: string
  }
  listing: {
    id: string
    full_address: string
    rent: number
    application_url: string
  }
  customer: {
    first_name: string
    last_name: string
    email: string
  }
}

interface CustomerUsage {
  customer: {
    id: string
    first_name: string
    last_name: string
    email: string
  }
  last_used_at: string
}

interface GlobalStats {
  [featureType: string]: number
}

type CustomerOnlyStats = GlobalStats;
type AnonymousStats = GlobalStats;

type TabPanelProps = {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`task-tabpanel-${index}`}
      aria-labelledby={`task-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

export default function AdminTasks() {

  const { props } = usePage<{ 
    tasks: Task[]
    pending_tasks: Task[]
    completed_tasks: Task[]
  }>()
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [changes, setChanges] = useState<Record<string, Partial<Task>>>({})
  const [error, setError] = useState<string | null>(null)
  const [tabValue, setTabValue] = useState(0)
  const [completingTask, setCompletingTask] = useState<string | null>(null)
  
  // Feature usage state
  const [customerOnlyStats, setCustomerOnlyStats] = useState<CustomerOnlyStats>({})
  const [anonymousStats, setAnonymousStats] = useState<AnonymousStats>({})
  const [customerSummaries, setCustomerSummaries] = useState<CustomerUsage[]>([])
  const [loadingUsage, setLoadingUsage] = useState(false)

  
  const fetchFeatureUsage = async () => {
    try {
      setLoadingUsage(true)
      const response = await axiosClient.get('/admin/feature_usage')
      setAnonymousStats(response.data.anonymous_stats || {})
      setCustomerOnlyStats(response.data.customer_only_stats || {})
      setCustomerSummaries(response.data.customer_summaries)
    } catch (error) {
      console.error('Failed to fetch feature usage:', error)
      setError('Failed to load feature usage data')
    } finally {
      setLoadingUsage(false)
    }
  }

  const completeTask = async (id: string) => {
    try {
      setCompletingTask(id)
      await axiosClient.patch(`/admin/tasks/${id}/complete_task`)
      // Refresh the tasks list to get updated data
      router.reload({ only: ['pending_tasks', 'completed_tasks'] })
    } catch (err) {
      setError("Failed to complete task. Please try again.")
      console.error("Error completing task:", err)
    } finally {
      setCompletingTask(null)
    }
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
    // Fetch feature usage data when switching to the feature usage tab
    if (newValue === 2) {
      fetchFeatureUsage()
    }
  }


  const getTaskTypeLabel = (actionType: string) => {
    switch (actionType) {
      case 'send_application':
        return 'Send Application'
      case 'create_listing':
        return 'Create Listing'
      default:
        return actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const TaskCard = ({ task }: { task: Task }) => (
    <Card sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <CardContent sx={{padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              {getTaskTypeLabel(task.action_type)}
            </Typography>
            <Chip 
              label={task.done ? "Completed" : "Pending"} 
              color={task.done ? "success" : "warning"}
              icon={task.done ? <CheckCircle /> : <Pending />}
              size="small"
            />
          </Box>
          {!task.done && (
            <Button
              variant="contained"
              size="small"
              onClick={() => completeTask(task.id)}
              disabled={completingTask === task.id}
              startIcon={completingTask === task.id ? <CircularProgress size={16} /> : <CheckCircle />}
              sx={{ 
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#45a049' }
              }}
            >
              {completingTask === task.id ? 'Completing...' : 'Mark as Complete'}
            </Button>
          )}
        </Box>

        <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: 'black', mb: 1 }}>
                Customer
                </Typography>
                <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                <strong>Name:</strong> {task.customer?.first_name} {task.customer?.last_name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                <strong>Email:</strong> {task.customer?.email}
                </Typography>
            </Grid>

            {task.prospect && (
            <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ color: 'black', mb: 1 }}>
                    Prospect
                </Typography>
                <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                    <strong>Name:</strong> {task.prospect.name}
                </Typography>
                <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                    <strong>Email:</strong> {task.prospect.email}
                </Typography>
                <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                    <strong>Phone:</strong> {formatPhoneNumber(task.prospect.phone_number)}
                </Typography>
            </Grid>
            )}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box>
          <Typography variant="subtitle2" sx={{ color: 'black', mb: 1 }}>
            Listing Details
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Address:</strong> {task.listing?.full_address}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Rent:</strong> ${task.listing?.rent?.toLocaleString()}
          </Typography>
          {task.listing?.application_url && (
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Application URL:</strong>{' '}
              <a 
                href={task.listing.application_url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#1976d2', textDecoration: 'none' }}
              >
                View Application
              </a>
            </Typography>
          )}
        </Box>

        {task.done && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ color: 'black', mb: 1 }}>
                Completion Details
              </Typography>
              <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                <strong>Completed by:</strong> {task.done_by}
              </Typography>
              <Typography variant="body2" sx={{ color: 'black', mb: 0.5 }}>
                <strong>Completed at:</strong> {task.executed_at ? formatDate(task.executed_at) : 'N/A'}
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
  
  return (
    <Box sx={{ p: 3, maxWidth: '1200px', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Task Manager
        </Typography>
        <Tooltip title="Refresh tasks">
          <IconButton onClick={() => router.reload()}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="task tabs">
        <Tab label={`Pending Tasks (${(props.pending_tasks?.length || 0)})`} id="task-tab-0" aria-controls="task-tabpanel-0" />
        <Tab label={`Completed Tasks (${(props.completed_tasks?.length || 0)})`} id="task-tab-1" aria-controls="task-tabpanel-1" />
        <Tab label="Feature Usage" id="task-tab-2" aria-controls="task-tabpanel-2" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
      {(props.pending_tasks?.length || 0) === 0 ? (
      <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No pending tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All tasks have been completed!
            </Typography>
          </Box>
        ) : (
          <>
            {/* General Tasks */}
            {props.pending_tasks
                ?.filter((task) => task.action_type !== 'create_listing')
                .map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}

            {/* Divider only if both types exist */}
            {props.pending_tasks?.some((task) => task.action_type === 'create_listing') &&
              props.pending_tasks?.some((task) => task.action_type !== 'create_listing') && (
                <Divider sx={{ my: 4, borderBottomWidth: 2, borderColor: 'black' }} />
              )}
          </>
        )}
        </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {/* Check if there are no completed tasks */}
        {(props.completed_tasks?.length || 0) === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Pending sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No completed tasks
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete some tasks to see them here
            </Typography>
          </Box>
        ) : (
          <>
            {/* General Tasks */}
            {props.completed_tasks
                ?.filter((task) => task.action_type !== 'create_listing')
                .map((task) => (
                  <TaskCard key={task.id} task={task} />
                ))}

            {/* Divider only if both types exist */}
            {props.completed_tasks?.some((task) => task.action_type === 'create_listing') &&
              props.completed_tasks?.some((task) => task.action_type !== 'create_listing') && (
                <Divider sx={{ my: 4, borderBottomWidth: 2, borderColor: 'black' }} />
              )}
          </>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {loadingUsage ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Anonymous-only Stats Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Analytics sx={{ mr: 1 }} />
                  Anonymous Feature Usage
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(anonymousStats).filter(([, count]) => count > 0).map(([feature, count]) => (
                    <Grid item xs={12} sm={6} md={4} key={feature}>
                      <Box sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                          {count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Customer-only Stats Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <Analytics sx={{ mr: 1 }} />
                  Logged-in Customer Feature Usage
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(customerOnlyStats).filter(([, count]) => count > 0).map(([feature, count]) => (
                    <Grid item xs={12} sm={6} md={4} key={feature}>
                      <Box sx={{ 
                        p: 2, 
                        border: '1px solid #e0e0e0', 
                        borderRadius: 1,
                        textAlign: 'center'
                      }}>
                        <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>
                          {count}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Customer Usage Cards */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              Customer Activity
            </Typography>
            <Grid container spacing={2}>
              {customerSummaries.map((summary) => (
                <Grid item xs={12} sm={6} md={4} key={summary.customer.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>
                        {summary.customer.first_name} {summary.customer.last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {summary.customer.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Last activity: {new Date(summary.last_used_at).toLocaleDateString()}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => router.visit(`/admin/feature_usage/customer/${summary.customer.id}`)}
                      >
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
      </TabPanel>
    </Box>
  );
}